import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft"
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight"
import Box from "@mui/material/Box"
import IconButton from "@mui/material/IconButton"
import Typography from "@mui/material/Typography"
import { ActivityIndicator } from "components/activity-indicator"
import React, { ReactNode, useCallback, useRef } from "react"
import { makeStyles } from "tss-react/mui-compat"

const useStyles = makeStyles()(
  (theme) => ({
    root: {
      marginTop: "20px",
    },
    header: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-end",
    },
    titleBox: {
      marginRight: "40px",
      paddingBottom: 20,
    },
    title: {
      ...theme.fonts.raleway700,
      fontSize: 18,
      paddingLeft: theme.spacing(0.1),
    },
    buttonWrapper: {
      display: "inline-flex",
      alignSelf: "flex-end",
      paddingBottom: 8,
    },
    horizontalScrollBox: {
      display: "flex",
      paddingLeft: theme.spacing(0.1),
      overflowX: "scroll",
      overflowY: "hidden",
      "&::-webkit-scrollbar": {
        display: "none",
      },
    },
    loadingIndicatorContainer: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      minHeight: "100%",
      paddingLeft: theme.spacing(4),
      paddingRight: theme.spacing(4),
    },
    lastItem: {
      width: 20,
      height: "100%",
    },
  })
)

export interface InfiniteHorizontalScrollerProps {
  title: string
  emptyStateMessage: string
  scrollSpeed?: number
  children: ReactNode[]
  hasMore: boolean
  loadMore: () => void
  loading: boolean
}

const InfiniteHorizontalScroller = (props: InfiniteHorizontalScrollerProps) => {
  const {
    title,
    emptyStateMessage,
    scrollSpeed = 250,
    children,
    hasMore,
    loadMore,
    loading,
  } = props

  const horizontalScrollRef = useRef<HTMLDivElement>(null)

  const { classes } = useStyles()

  const horizontalScrollLeft = () => {
    if (horizontalScrollRef.current) {
      horizontalScrollRef.current.scroll({
        left: horizontalScrollRef.current.scrollLeft - scrollSpeed,
        behavior: "smooth",
      })
    }
  }

  const horizontalScrollRight = () => {
    if (horizontalScrollRef.current) {
      horizontalScrollRef.current.scroll({
        left: horizontalScrollRef.current.scrollLeft + scrollSpeed,
        behavior: "smooth",
      })
    }
  }

  const observer = useRef<IntersectionObserver>(null)
  const lastItemRef = useCallback(
    (node: HTMLElement | null) => {
      if (loading) return
      if (observer.current) observer.current.disconnect()
      // @ts-ignore
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) loadMore()
      })
      if (node) observer.current.observe(node)
    },
    [loading, hasMore],
  )

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Box className={classes.titleBox}>
          <Typography variant="h4" className={classes.title}>
            {title}
          </Typography>
        </Box>
        <Box className={classes.buttonWrapper}>
          <IconButton aria-label="Scroll left" onClick={horizontalScrollLeft} size="large">
            <KeyboardArrowLeftIcon />
          </IconButton>
          <IconButton aria-label="Scroll right" onClick={horizontalScrollRight} size="large">
            <KeyboardArrowRightIcon />
          </IconButton>
        </Box>
      </Box>

      <Box className={classes.horizontalScrollBox} {...{ ref: horizontalScrollRef }}>
        {children.length === 0 && !loading && (
          <Typography style={{ marginTop: "8px" }}>{emptyStateMessage}</Typography>
        )}
        {children}
        {!loading && hasMore && <Box className={classes.lastItem} {...{ ref: lastItemRef }} />}
        {(loading || hasMore) && (
          <Box className={classes.loadingIndicatorContainer}>
            <ActivityIndicator indicatorSize={25} />
          </Box>
        )}
      </Box>
    </Box>
  )
}

export default InfiniteHorizontalScroller
